import { useEffect, useState, type MouseEventHandler, type ReactNode, type Ref } from 'react'
import { useBreakpoint, type Tier } from '../../hooks/useBreakpoint'

/**
 * UI-002 — shared control band.
 *
 * The audit's SYSTEM-02 finding (toolbar and filter congestion) is the same on
 * every dense screen: eight to ten controls of equal weight sit above the data,
 * wrap into two or three rows as the viewport narrows, and push the actual task
 * below the fold. Hiding controls is not the answer — operators use them — so
 * this component ranks them instead.
 *
 *   primary   always visible: view tabs, search, the one filter the task needs
 *   more      inline while there is room; below `collapseAt` it folds behind a
 *             single "More filters" disclosure that carries an active count, so
 *             nothing is silently lost and nothing is silently applied
 *   trailing  right-aligned and always visible: counts, legends, scope readouts
 *
 * `collapseAt` is a viewport tier by default, but a filter bar living inside a
 * narrow side pane (Command Center's feed column is 268–360px) has no room at
 * any viewport width — those pass `collapseAt="always"`.
 */
export type FilterBarCollapse = Tier | 'always' | 'never'

/**
 * One demoted filter that is currently set. Rendered as a removable chip beside
 * the disclosure so the user can see WHICH filters are on without opening the
 * tray — the count alone answers "how many", not "which", and a filter you
 * cannot name is a filter you cannot trust.
 */
export interface ActiveFilterChip {
  key: string
  label: string
  onRemove?: () => void
}

interface FilterBarProps {
  primary: ReactNode
  /** Lower-priority controls. Folded behind the disclosure once collapsed. */
  more?: ReactNode
  /** How many of `more`'s controls are currently set — drives the badge.
      Ignored when `activeFilters` is supplied; that list is more specific. */
  moreActiveCount?: number
  /** The demoted filters that are set, named. Shown as removable chips while
      collapsed, so the tray never has to be opened just to read state. */
  activeFilters?: ActiveFilterChip[]
  /** How many chips to show before collapsing the rest into "+N". */
  maxActiveChips?: number
  /** Shown as "Clear" inside the expanded tray when anything is active. */
  onClearMore?: () => void
  /** Right-aligned, never collapsed. */
  trailing?: ReactNode
  collapseAt?: FilterBarCollapse
  paddingX?: number
  paddingY?: number
  moreLabel?: string
  /** For pages that already run a click-outside guard over their filter bar. */
  containerRef?: Ref<HTMLDivElement>
  onClick?: MouseEventHandler<HTMLDivElement>
  'data-testid'?: string
}

export function FilterBar({
  primary,
  more,
  moreActiveCount = 0,
  activeFilters,
  maxActiveChips = 2,
  onClearMore,
  trailing,
  collapseAt = 'compact',
  paddingX = 24,
  paddingY = 8,
  moreLabel = 'More filters',
  containerRef,
  onClick,
  'data-testid': testId,
}: FilterBarProps) {
  const bp = useBreakpoint()
  const collapsed =
    collapseAt === 'always' ? true : collapseAt === 'never' ? false : bp.atMost(collapseAt)

  const [open, setOpen] = useState(false)

  const activeCount = activeFilters ? activeFilters.length : moreActiveCount
  // When the chips are present they ARE the state readout, so the disclosure
  // stops competing with them and goes quiet. Without chips the button is the
  // only place state can live, so it takes the accent and says the count.
  const buttonCarriesState = activeCount > 0 && !activeFilters
  const shownChips = activeFilters ? activeFilters.slice(0, maxActiveChips) : []
  const hiddenChipCount = activeFilters ? activeFilters.length - shownChips.length : 0

  // Widening past the tier makes the tray meaningless — the controls are back
  // inline. Leaving `open` set would re-expand an empty tray on the next resize.
  useEffect(() => {
    if (!collapsed) setOpen(false)
  }, [collapsed])

  const showTray = collapsed && !!more && open

  return (
    <div
      ref={containerRef}
      onClick={onClick}
      data-testid={testId}
      data-page-controls=""
      style={{
        borderBottom: '1px solid var(--border)',
        background: 'var(--surface-2)',
        flexShrink: 0,
        position: 'relative',
        zIndex: 20,
      }}
    >
      <div className="l-wrap" style={{ gap: 6, rowGap: 8, padding: `${paddingY}px ${paddingX}px` }}>
        {primary}

        {collapsed && more ? (
          <button
            type="button"
            data-testid="filterbar-more"
            aria-expanded={open}
            onClick={() => setOpen(o => !o)}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '5px 11px', borderRadius: 'var(--r-sm)',
              fontSize: 11, fontWeight: 600, fontFamily: 'var(--sans)',
              cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
              border: `1.5px solid ${buttonCarriesState ? 'var(--accent)' : 'var(--border-2)'}`,
              background: buttonCarriesState ? 'var(--accent-soft)' : 'var(--surface)',
              color: buttonCarriesState ? 'var(--accent)' : 'var(--text-2)',
              transition: 'var(--transition)',
            }}
          >
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="4" y1="6" x2="20" y2="6" /><line x1="7" y1="12" x2="17" y2="12" /><line x1="10" y1="18" x2="14" y2="18" />
            </svg>
            {buttonCarriesState ? `${activeCount} filter${activeCount === 1 ? '' : 's'} on` : moreLabel}
            <svg
              width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
              style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .15s ease' }}
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>
        ) : (
          more
        )}

        {/* Named state, collapsed. Removing a chip clears that one filter. */}
        {collapsed && shownChips.length > 0
          ? shownChips.map(chip => (
              <span
                key={chip.key}
                data-testid="filterbar-chip"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '4px 8px', borderRadius: 'var(--r-xs)',
                  background: 'var(--surface)', color: 'var(--text-1)',
                  border: '1px solid var(--border-2)',
                  fontFamily: 'var(--sans)', fontSize: 10.5, fontWeight: 600,
                  whiteSpace: 'nowrap', maxWidth: 190, flexShrink: 0,
                }}
              >
                <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--accent)', flexShrink: 0 }} />
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{chip.label}</span>
                {chip.onRemove ? (
                  <button
                    type="button"
                    aria-label={`Remove filter ${chip.label}`}
                    onClick={chip.onRemove}
                    style={{
                      background: 'none', border: 'none', padding: 0, lineHeight: 1,
                      cursor: 'pointer', color: 'var(--text-3)', fontSize: 12,
                    }}
                  >
                    ×
                  </button>
                ) : null}
              </span>
            ))
          : null}

        {collapsed && hiddenChipCount > 0 ? (
          <button
            type="button"
            onClick={() => setOpen(true)}
            style={{
              background: 'none', border: 'none', padding: 0, cursor: 'pointer',
              fontFamily: 'var(--sans)', fontSize: 10.5, fontWeight: 700,
              color: 'var(--accent)', whiteSpace: 'nowrap', flexShrink: 0,
            }}
          >
            +{hiddenChipCount} more
          </button>
        ) : null}

        {trailing ? (
          <div className="l-wrap" style={{ marginLeft: 'auto', gap: 10, justifyContent: 'flex-end' }}>
            {trailing}
          </div>
        ) : null}
      </div>

      {showTray ? (
        <div
          className="l-wrap"
          data-testid="filterbar-tray"
          style={{
            gap: 6, rowGap: 8,
            padding: `${paddingY}px ${paddingX}px`,
            borderTop: '1px solid var(--border)',
            background: 'var(--surface)',
          }}
        >
          {more}
          {onClearMore && activeCount > 0 ? (
            <button
              type="button"
              onClick={onClearMore}
              style={{
                marginLeft: 'auto', background: 'none', border: 'none', padding: 0,
                cursor: 'pointer', fontFamily: 'var(--sans)', fontSize: 11,
                fontWeight: 700, color: 'var(--accent)', whiteSpace: 'nowrap',
              }}
            >
              Clear {activeCount}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}
